// ** Third Party Components
import styled, { css } from "styled-components";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import Icon from "components/global/Icon";
import Link from "components/layout/Link";
import { StyledOverlay } from "styles/components/Overlay.styled";

// ** Utils & Helpers
import pseudoFormat from "utils/pseudoFormat";

// ** SVG Static Imports
import { ReactComponent as SolidLock } from "resources/icons/solid/lock.svg";
import { ReactComponent as SolidUnlock } from "resources/icons/solid/unlock.svg";

const AlbumContainerDisabled = styled(Block)`
	cursor: pointer;
	position: relative;
	width: 100%;
	padding-bottom: 100%;
	overflow: hidden;
	background-color: var(--dz-ref-color-gray100);
	transition: transform 100ms;
	transform: scale(1);
	border-radius: var(--dz-sys-card-border-radius);

	&:hover {
		img {
			transform: scale(1.05);
		}
	}

	&:visited {
		color: var(--dz-ref-color-white);
	}

	${({ isActive }) =>
		isActive &&
		css`
			font-weight: bold;
		`}
`;

const StyledLinkContainer = styled(Link)`
	position: relative;
	padding-bottom: 100%;
	width: 100%;
	overflow: hidden;
	background-color: var(--dz-ref-color-gray100);
	border-radius: var(--dz-sys-card-border-radius);

	transition: transform 100ms;
	transform: scale(1);

	&:hover {
		img {
			transform: scale(1.05);
		}
	}

	${({ $isActive }) =>
		$isActive &&
		css`
			font-weight: bold;
		`}
`;

const StyledActiveElement = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	pointer-events: none;
	background-color: transparent;
	z-index: 10;
	border-radius: var(--dz-sys-card-border-radius);

	${({ $isActive }) =>
		$isActive &&
		css`
			border: 3px solid
				${({ theme: { isDarkMode } }) => (isDarkMode ? "#FFF" : "#000")};
		`}
`;

const AlbumContainerContent = styled.div`
	position: absolute;
	top: 0;
	width: 100%;
	height: 100%;
	overflow: hidden;
	color: ${({ theme: { colors } }) => colors.whiteAlways};
`;

const AlbumDataContainer = styled.div`
	position: absolute;
	bottom: 0;
	padding: 0.5rem 0.75rem;
	background: linear-gradient(
		180deg,
		rgba(0, 0, 0, 0) 0%,
		rgba(0, 0, 0, 1) 100%
	);
	z-index: 3;
	font-size: 11.9px;
	display: flex;
	width: 100%;
	text-overflow: ellipsis;
	overflow: hidden;
	svg {
		color: var(--color-whiteTrueAlways-darken-200);
	}
`;

const StyledImage = styled.img`
	display: flex;
	max-height: 100%;
	max-width: 100%;
	overflow: hidden;

	transition: transform 100ms;
	transform: scale(1);

	${({ $isActive }) =>
		$isActive &&
		css`
			box-sizing: border-box;
			border: 3px solid transparent;
		`}

	${({ isBlurred }) =>
		isBlurred &&
		css`
			filter: blur(7px);
			filter: progid:DXImageTransform.Microsoft.Blur(pixelradius='7', shadowopacity='0.0');
			-ms-filter: blur(7px);
			-webkit-filter: blur(7px);
		`}
`;

// #####################################################

const NavImageAlbumLink = ({
	isActive,
	album,
	to,
	isDisabled = false,
	isAuth,
	user,
	defaultAvatar,
	isMe,
	onClick,
}) => {
	const renderContent = () => {
		return (
			<>
				<AlbumContainerContent>
					<AlbumDataContainer>
						<Block fullWidth flex justifyBetween>
							<Text
								style={{
									display: "flex",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								<Text
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{album.private &&
									(user?.status?.friendStatus === 3 ||
										isMe) ? (
										<Text mr={1}>
											<Icon
												svgIconComponent={SolidUnlock}
												height="12px"
											/>
										</Text>
									) : (
										album.private && (
											<Text mr={1}>
												<Icon
													svgIconComponent={SolidLock}
													height="12px"
												/>
											</Text>
										)
									)}{" "}
									{album.name}
								</Text>
							</Text>
							<Block textNowrap ml={1}>
								<Text>
									{album.amount > 0 &&
										pseudoFormat(album.amount)}
								</Text>
							</Block>
						</Block>
					</AlbumDataContainer>
					<StyledOverlay style={{ zIndex: 3 }} />
					<StyledImage
						src={
							album?.image?.media?.square ||
							album.image?.media?.poster_square ||
							defaultAvatar
						}
						$isActive={isActive}
						isBlurred={!isAuth}
					/>
				</AlbumContainerContent>
				<StyledActiveElement $isActive={isActive} />
			</>
		);
	};

	if (isDisabled) {
		return (
			<AlbumContainerDisabled disabled={isDisabled}>
				{renderContent()}
			</AlbumContainerDisabled>
		);
	} else {
		return (
			<StyledLinkContainer
				$isActive={isActive}
				to={to}
				noScroll
				onClick={onClick}
			>
				{renderContent()}
			</StyledLinkContainer>
		);
	}
};

export default NavImageAlbumLink;
